import ReportingIcon from '@mui/icons-material/BarChart';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import {Box, Drawer, Stack} from '@mui/material';
import {forwardRef} from 'react';
import {useTranslation} from '../i18n/hooks/useTranslation';
import {useLayoutOffset} from '../layout/hooks/useLayoutOffset';
import {PropsWithoutChildren} from '../react/types';
import {useDeviceState} from '../responsive/hooks/useDeviceState';
import {Paths} from '../routes/paths';
import {NavigationMenu} from './NavigationMenu';
import {NavigationMenuEntry} from './types';

interface NarrowNavigationProps extends PropsWithoutChildren {
  isNarrowNavigationOpen: boolean;
  onNarrowNavigationClose: () => void;
}
type WideNavigationProps = PropsWithoutChildren;
type PrivateSideNavigationProps = NarrowNavigationProps & WideNavigationProps;

// Add menu items to the list below.
// Each sub-array of items will be separated by a divider.
export const menuItems: NavigationMenuEntry[][] = [
  [
    {iconType: DashboardIcon, path: Paths.Dashboard, translationKey: 'main:pages.dashboard'},
    {iconType: ReportingIcon, path: Paths.Reporting, translationKey: 'main:pages.reporting'},
  ],
  [{iconType: SettingsIcon, path: Paths.Settings, translationKey: 'main:pages.settings'}],
];

export const PrivateSideNavigation = forwardRef<HTMLElement, PrivateSideNavigationProps>(
  ({isNarrowNavigationOpen, onNarrowNavigationClose}: PrivateSideNavigationProps, ref) => {
    const {isNarrow} = useDeviceState();
    const {translate} = useTranslation('main');
    return (
      <Box aria-label={translate('main:sideNavigation')} component="nav" role="navigation">
        {isNarrow && <NarrowNavigation {...{isNarrowNavigationOpen, onNarrowNavigationClose, ref}} />}
        {!isNarrow && <WideNavigation {...{ref}} />}
      </Box>
    );
  },
);
PrivateSideNavigation.displayName = 'SideNavigation';

const NarrowNavigation = forwardRef<HTMLElement, NarrowNavigationProps>(
  ({isNarrowNavigationOpen, onNarrowNavigationClose}: NarrowNavigationProps, ref) => {
    return (
      <Drawer
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        data-testid="PrivateSideNavigation"
        onClose={onNarrowNavigationClose}
        open={isNarrowNavigationOpen}
        variant="temporary"
      >
        <InnerNavigationMenu closeMenu={onNarrowNavigationClose} ref={ref} />
      </Drawer>
    );
  },
);
NarrowNavigation.displayName = 'NarrowNavigation';

const WideNavigation = forwardRef<HTMLElement, WideNavigationProps>((_props: WideNavigationProps, ref) => {
  return (
    <Drawer data-testid="PrivateSideNavigation" open variant="permanent">
      <InnerNavigationMenu ref={ref} />
    </Drawer>
  );
});
WideNavigation.displayName = 'WideNavigation';

type AnyNavigationProps = (NarrowNavigationProps | WideNavigationProps) & {closeMenu?: () => void};
const InnerNavigationMenu = forwardRef<HTMLElement, AnyNavigationProps>(({closeMenu}: AnyNavigationProps, ref) => {
  const {verticalOffset} = useLayoutOffset(); // Take us to the bottom of the top bar
  return (
    <Stack sx={{mt: `${verticalOffset}px`}}>
      <NavigationMenu closeMenu={closeMenu} menuItems={menuItems} ref={ref} sx={{mt: 2}} />
    </Stack>
  );
});
InnerNavigationMenu.displayName = 'InnerNavigationMenu';
